import { StaffTicketStats, TicketStatus } from "@signalist/shared-types";

/**
 * The staff tabs (PREC-3939 decision 5): the mockup's partition. Needs action is what Signalist
 * owes, All open adds what the customer owes; Mine and Unassigned cut by assignee. A tab is a
 * preset of statuses plus an assignee rule; the filter row narrows inside it.
 */
export enum StaffTicketTab {
	NEEDS_ACTION = "NEEDS_ACTION",
	MINE = "MINE",
	UNASSIGNED = "UNASSIGNED",
	ALL_OPEN = "ALL_OPEN",
	RESOLVED = "RESOLVED",
	CLOSED = "CLOSED",
}

/** Render order of the tab strip. */
export const STAFF_TICKET_TABS: StaffTicketTab[] = [
	StaffTicketTab.NEEDS_ACTION,
	StaffTicketTab.MINE,
	StaffTicketTab.UNASSIGNED,
	StaffTicketTab.ALL_OPEN,
	StaffTicketTab.RESOLVED,
	StaffTicketTab.CLOSED,
];

export type TabAssigneeRule = "ANY" | "MINE" | "UNASSIGNED";

export interface StaffTabPreset {
	statuses: TicketStatus[];
	assignee: TabAssigneeRule;
}

const { OPEN, WAITING_ON_CUSTOMER, WAITING_ON_US, RESOLVED, CLOSED } = TicketStatus;

export const STAFF_TAB_PRESETS: Record<StaffTicketTab, StaffTabPreset> = {
	[StaffTicketTab.NEEDS_ACTION]: { statuses: [OPEN, WAITING_ON_US], assignee: "ANY" },
	// The API's default queue statuses — everything still on the table.
	[StaffTicketTab.MINE]: {
		statuses: [OPEN, WAITING_ON_CUSTOMER, WAITING_ON_US, RESOLVED],
		assignee: "MINE",
	},
	[StaffTicketTab.UNASSIGNED]: {
		statuses: [OPEN, WAITING_ON_CUSTOMER, WAITING_ON_US],
		assignee: "UNASSIGNED",
	},
	[StaffTicketTab.ALL_OPEN]: {
		statuses: [OPEN, WAITING_ON_CUSTOMER, WAITING_ON_US],
		assignee: "ANY",
	},
	[StaffTicketTab.RESOLVED]: { statuses: [RESOLVED], assignee: "ANY" },
	[StaffTicketTab.CLOSED]: { statuses: [CLOSED], assignee: "ANY" },
};

export const isAssigneeLocked = (tab: StaffTicketTab): boolean =>
	STAFF_TAB_PRESETS[tab].assignee !== "ANY";

/** What the status control may offer on a tab — its own statuses, nothing outside. */
export const statusOptionsForTab = (tab: StaffTicketTab): TicketStatus[] =>
	STAFF_TAB_PRESETS[tab].statuses;

const TAB_COUNT_KEY: Record<StaffTicketTab, keyof StaffTicketStats["tabs"]> = {
	[StaffTicketTab.NEEDS_ACTION]: "needsAction",
	[StaffTicketTab.MINE]: "mine",
	[StaffTicketTab.UNASSIGNED]: "unassigned",
	[StaffTicketTab.ALL_OPEN]: "allOpen",
	[StaffTicketTab.RESOLVED]: "resolved",
	[StaffTicketTab.CLOSED]: "closed",
};

export type StaffTabCounts = Record<StaffTicketTab, number | null>;

/** Whole-queue counts from the stats read; null per tab until it has arrived. */
export const staffTabCounts = (stats: StaffTicketStats | undefined): StaffTabCounts =>
	STAFF_TICKET_TABS.reduce((counts, tab) => {
		counts[tab] = stats ? Number(stats.tabs[TAB_COUNT_KEY[tab]] ?? 0) || 0 : null;

		return counts;
	}, {} as StaffTabCounts);
